import { booleanAttribute, Component, computed, input } from '@angular/core';
import { getAvatarColor } from 'utilities';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const AVATAR_SIZE_MAP: Record<AvatarSize, { dimension: string; fontSize: string }> = {
  xs: { dimension: '8px', fontSize: '0' },
  sm: { dimension: '14px', fontSize: '0.65625rem' },
  md: { dimension: '18px', fontSize: '0.5rem' },
  lg: { dimension: '36px', fontSize: '1rem' },
};

@Component({
  selector: 'ocp-avatar',
  templateUrl: './avatar.component.html',
  styles: `
    :host {
      display: flex;
    }

    .avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      line-height: 1;
      flex-shrink: 0;
      width: var(--corporate-avatar-width);
      height: var(--corporate-avatar-height);
      font-weight: var(--font-weight-bold);
    }
  `,
  host: {
    '[attr.aria-hidden]': 'singleInitial() || !userInitials() ? "true" : null',
  },
})
export class AvatarComponent {
  readonly userInitials = input('');
  readonly size = input<AvatarSize | undefined>(undefined);
  readonly singleInitial = input(false, { transform: booleanAttribute });
  /** Overrides the font-size derived from `size`. */
  readonly fontSize = input<string | undefined>(undefined);
  /** Overrides the font-weight derived from `singleInitial`. */
  readonly fontWeight = input<string | undefined>(undefined);
  /** Entity ID used for deterministic color selection. Falls back to userInitials if not provided. */
  readonly colorSeed = input<string | undefined>(undefined);
  /** When true, shows a neutral gray color instead of the deterministic color (e.g. for pending/inactive users). */
  readonly muted = input(false, { transform: booleanAttribute });

  protected readonly displayLabel = computed(() => {
    const initials = this.userInitials();
    return this.singleInitial() ? initials?.charAt(0) : initials;
  });

  protected readonly avatarStyle = computed(() => {
    const preset = this.size();
    const sizeConfig = preset ? AVATAR_SIZE_MAP[preset] : undefined;
    const style: Record<string, string> = {};
    if (sizeConfig) {
      style['width'] = sizeConfig.dimension;
      style['height'] = sizeConfig.dimension;
      style['font-size'] = this.fontSize() ?? sizeConfig.fontSize;
    }

    if (this.muted()) {
      style['background-color'] = 'var(--corporate-button-disabled-background)';
      style['color'] = 'var(--corporate-text-secondary)';
    } else {
      const seed = this.colorSeed() ?? this.userInitials();
      style['background-color'] = getAvatarColor(seed);
      style['color'] = 'var(--corporate-avatar-text)';
    }

    const fontWeight =
      this.fontWeight() ?? (this.singleInitial() ? 'var(--font-weight-bold)' : undefined);
    if (fontWeight) {
      style['font-weight'] = fontWeight;
    }

    return style;
  });
}
